import { cn } from '@/lib/utils'

/**
 * PressOne LogoMark.
 *
 * Renders the official PressOne icon (served from /brand/pressone-icon.svg)
 * alongside the "PressOne" wordmark and an optional sub-brand tag — here,
 * "AI" — coloured with the brand-blue so the product reads as a member of
 * the PressOne family.
 *
 * Patterned on the sister product (pressone-esim) so customers who know
 * one product recognise the other immediately. The icon already carries
 * the navy + brand-blue + gold + green + red-orange colour set, which is
 * the whole point of using it as the primary brand cue rather than an
 * abstract letter mark.
 */
type LogoMarkProps = {
  /** Pixel height of the icon. The wordmark scales with it. */
  size?: number
  /** Optional sub-brand label (e.g. "AI", "Workspace"). */
  tag?: string
  /** When true, the wordmark is rendered in white — for dark surfaces. */
  inverted?: boolean
  /** When true, only the icon is shown (no wordmark or tag). */
  iconOnly?: boolean
  className?: string
}

export function LogoMark({
  size = 32,
  tag = 'AI',
  inverted = false,
  iconOnly = false,
  className,
}: LogoMarkProps) {
  return (
    <span className={cn('inline-flex items-center gap-2.5', className)}>
      <img
        src="/brand/pressone-icon.svg"
        alt="PressOne"
        width={size}
        height={size}
        className="shrink-0"
        style={{ borderRadius: size / 4 }}
      />
      {!iconOnly && (
        <span className="inline-flex items-baseline gap-1.5 leading-none">
          <span
            className={cn(
              'font-semibold tracking-tight',
              inverted ? 'text-white' : 'text-text-strong',
            )}
            style={{ fontSize: Math.round(size * 0.5) }}
          >
            PressOne
          </span>
          {tag && (
            <span
              className="font-semibold tracking-tight text-brand-blue"
              style={{ fontSize: Math.round(size * 0.5) }}
            >
              {tag}
            </span>
          )}
        </span>
      )}
    </span>
  )
}
